import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

// Simple Linear Regression
// y = mx + b
const calculateLinearRegression = (data, xKey, yKey) => {
  const n = data.length;
  if (n === 0) return { m: 0, b: 0 };

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  data.forEach((point) => {
    const x = point[xKey];
    const y = point[yKey];
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  });

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return { m: 0, b: sumY / n };

  const m = (n * sumXY - sumX * sumY) / denominator;
  const b = (sumY - m * sumX) / n;

  return { m, b };
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#e1ebf0] rounded-2xl shadow-xl px-4 py-3">
        <p className="text-xs font-black text-[#8a7658] uppercase tracking-widest mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-black" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes("Revenue") ? "₱" : ""}
            {Number(entry.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminForecasting = ({ appointments }) => {
  const [timeframe, setTimeframe] = useState("30days"); // "7days", "30days", "6months"

  const chartData = useMemo(() => {
    const now = new Date();
    const completedApts = appointments.filter(a => a.status === "completed");

    let historyData = [];
    let forecastSteps = 0;
    
    if (timeframe === "7days" || timeframe === "30days") {
      const daysHistory = timeframe === "7days" ? 14 : 60; // Look back twice the forecast period
      forecastSteps = timeframe === "7days" ? 7 : 30;
      
      // Build history
      for (let i = daysHistory; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        const dayApts = completedApts.filter(a => {
          const ap = new Date(a.createdAt);
          return ap.getFullYear() === d.getFullYear() && ap.getMonth() === d.getMonth() && ap.getDate() === d.getDate();
        });
        
        historyData.push({
          index: daysHistory - i, // x-value for regression
          dateObj: d,
          label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          revenue: dayApts.reduce((s, a) => s + (a.totalPrice || 0), 0),
          count: dayApts.length
        });
      }
    } else if (timeframe === "6months") {
      const monthsHistory = 12; // Look back 12 months
      forecastSteps = 6;
      
      for (let i = monthsHistory; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthApts = completedApts.filter(a => {
          const ap = new Date(a.createdAt);
          return ap.getFullYear() === d.getFullYear() && ap.getMonth() === d.getMonth();
        });
        
        historyData.push({
          index: monthsHistory - i,
          dateObj: d,
          label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
          revenue: monthApts.reduce((s, a) => s + (a.totalPrice || 0), 0),
          count: monthApts.length
        });
      }
    }

    // Calculate Regression
    const revenueReg = calculateLinearRegression(historyData, "index", "revenue");
    const countReg = calculateLinearRegression(historyData, "index", "count");

    // Combine history and forecast into one dataset for plotting
    const combinedData = historyData.map(d => ({
      ...d,
      actualRevenue: d.revenue,
      actualCount: d.count,
      forecastRevenue: null, // Don't show forecast line over history
      forecastCount: null
    }));

    // Connect the lines seamlessly by adding the last historical point to forecast
    if (combinedData.length > 0) {
      const lastPoint = combinedData[combinedData.length - 1];
      lastPoint.forecastRevenue = lastPoint.actualRevenue;
      lastPoint.forecastCount = lastPoint.actualCount;
    }

    // Add forecast steps
    const lastIndex = historyData[historyData.length - 1]?.index || 0;
    const lastDate = historyData[historyData.length - 1]?.dateObj || now;

    for (let i = 1; i <= forecastSteps; i++) {
      const newIndex = lastIndex + i;
      let newDate;
      let newLabel;

      if (timeframe === "6months") {
        newDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + i, 1);
        newLabel = newDate.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      } else {
        newDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate() + i);
        newLabel = newDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }

      // Linear regression formula: y = mx + b. 
      // Ensure we don't project negative numbers for business metrics.
      const projectedRevenue = Math.max(0, revenueReg.m * newIndex + revenueReg.b);
      const projectedCount = Math.max(0, countReg.m * newIndex + countReg.b);

      combinedData.push({
        index: newIndex,
        dateObj: newDate,
        label: newLabel + " (Est)",
        actualRevenue: null,
        actualCount: null,
        forecastRevenue: projectedRevenue,
        forecastCount: projectedCount
      });
    }

    return combinedData;
  }, [appointments, timeframe]);

  return (
    <div className="space-y-8 animate-fade-in">
      
      {/* Header and Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-3xl shadow-sm border border-[#e1ebf0]">
        <div>
          <h2 className="text-xl font-black text-gray-800">Business Forecast</h2>
          <p className="text-xs text-gray-400 mt-1">Predictive analysis based on historical appointment data using Linear Regression.</p>
        </div>
        <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-xl">
          {[
            { id: "7days", label: "Next 7 Days" },
            { id: "30days", label: "Next 30 Days" },
            { id: "6months", label: "Next 6 Months" }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setTimeframe(opt.id)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                timeframe === opt.id
                  ? "bg-[#6992a5] text-white shadow-md"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Revenue Forecast Chart */}
        <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
          <div className="mb-6">
            <h3 className="text-lg font-black text-[#46735c]">Revenue Projection</h3>
            <p className="text-xs text-gray-400">Historical vs Projected Earnings</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 10, fontWeight: 700, fill: "#8a7658" }} 
                axisLine={false} 
                tickLine={false} 
                minTickGap={20}
              />
              <YAxis 
                tick={{ fontSize: 10, fontWeight: 700, fill: "#8a7658" }} 
                axisLine={false} 
                tickLine={false} 
                tickFormatter={v => `₱${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
              
              <Line 
                type="monotone" 
                dataKey="actualRevenue" 
                name="Actual Revenue" 
                stroke="#46735c" 
                strokeWidth={3} 
                dot={{ r: 2 }} 
                activeDot={{ r: 6 }} 
              />
              <Line 
                type="monotone" 
                dataKey="forecastRevenue" 
                name="Projected Revenue" 
                stroke="#6992a5" 
                strokeWidth={3} 
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Appointment Count Forecast Chart */}
        <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
          <div className="mb-6">
            <h3 className="text-lg font-black text-[#a78bfa]">Appointment Volume</h3>
            <p className="text-xs text-gray-400">Historical vs Projected Bookings</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 10, fontWeight: 700, fill: "#8a7658" }} 
                axisLine={false} 
                tickLine={false} 
                minTickGap={20}
              />
              <YAxis 
                tick={{ fontSize: 10, fontWeight: 700, fill: "#8a7658" }} 
                axisLine={false} 
                tickLine={false} 
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
              
              <Line 
                type="monotone" 
                dataKey="actualCount" 
                name="Actual Bookings" 
                stroke="#a78bfa" 
                strokeWidth={3} 
                dot={{ r: 2 }} 
                activeDot={{ r: 6 }} 
              />
              <Line 
                type="monotone" 
                dataKey="forecastCount" 
                name="Projected Bookings" 
                stroke="#e0a458" 
                strokeWidth={3} 
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

      </div>
      
      {/* Information Alert */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start gap-4">
        <div className="text-2xl">📈</div>
        <div>
          <h4 className="text-sm font-black text-blue-800">How does this work?</h4>
          <p className="text-xs text-blue-600 mt-1 leading-relaxed">
            We look at your past completed appointments to figure out if your business is growing or slowing down. 
            Based on that trend, we guess how much revenue and how many bookings you might have in the future! 
            Just keep in mind that this is a rough estimate and doesn't predict sudden changes like holidays or unexpectedly busy seasons.
          </p>
        </div>
      </div>

    </div>
  );
};

export default AdminForecasting;
